/**
 * Cliente HTTP tipado del backend (contrato §0).
 *
 * Qué garantiza:
 * - `X-Surface` en **todo** request y `X-Sede-Activa` cuando hay sede activa (RN-ROL-08,
 *   RN-ROL-10): sin eso la auditoría del backend queda coja.
 * - Errores del contrato (`{ error: { code, message, details?, rule? } }`) convertidos en
 *   `ApiError`, con `rule` para trazabilidad (va en `data-rule` del toast, no se dibuja):
 *   el usuario lee el mensaje en castellano, no un código ni un "algo salió mal".
 * - `401` → refresh rotativo **en una sola cola** (los N requests que fallan a la vez
 *   esperan el mismo refresh) y reintento; si el refresh falla, se cierra la sesión.
 * - `403` → `ApiError` con `code: "FORBIDDEN"`; la pantalla «sin permiso» la decide el
 *   router. El intento ya quedó auditado del lado del backend (AF §11, caso borde).
 */
import type { ErrorResponse } from "@viva/shared";
import {
  clearSession,
  getAccessToken,
  getActiveBranchId,
  getRefreshToken,
  getSurface,
  setTokens,
  touchActivity,
} from "./auth/session.js";
import { PREVIEW_BLOCKED_MESSAGE, previewBlocks } from "./auth/preview.js";

export const API_URL: string =
  (import.meta.env?.VITE_API_URL as string | undefined)?.replace(/\/$/, "") ??
  "http://127.0.0.1:4800";

/** `PREVIEW_READ_ONLY`: escritura frenada en el navegador por la vista previa de un rol. */
export type ApiErrorCode = ErrorResponse["error"]["code"] | "NETWORK" | "PREVIEW_READ_ONLY";

export class ApiError extends Error {
  readonly code: ApiErrorCode;
  readonly status: number;
  readonly rule: string | undefined;
  readonly details: unknown;

  constructor(init: {
    code: ApiErrorCode;
    message: string;
    status: number;
    rule?: string | undefined;
    details?: unknown;
  }) {
    super(init.message);
    this.name = "ApiError";
    this.code = init.code;
    this.status = init.status;
    this.rule = init.rule;
    this.details = init.details;
  }

  /** `true` si el backend frenó la acción por una regla del AF (RN-XXX-NN). */
  get isRuleViolation(): boolean {
    return this.code === "RULE_VIOLATION";
  }
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  query?: Record<string, string | number | boolean | null | undefined>;
  /** Rutas de autenticación: no llevan Bearer ni entran en la cola de refresh. */
  anonymous?: boolean;
  signal?: AbortSignal | undefined;
  /** Respuesta sin JSON (204, CSV). */
  raw?: boolean;
  /**
   * Credencial de staff que NO es la sesión del navegador: la de quien opera la tablet de
   * recepción por un rato (`createStaffHttp`). Sin refresh ni cierre de la sesión global.
   */
  staff?: StaffCredential | undefined;
}

/**
 * Sesión de staff que vive sólo en memoria (la tablet de recepción, VTCS-91): token, sede
 * con la que opera y qué hacer cuando el backend la rechaza (venció o la cerraron).
 */
export interface StaffCredential {
  accessToken: string;
  branchId: string;
  onUnauthorized: () => void;
}

export function buildUrl(path: string, query?: RequestOptions["query"]): string {
  const url = new URL(path.startsWith("http") ? path : `${API_URL}${path}`);
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value === undefined || value === null || value === "") continue;
    url.searchParams.set(key, String(value));
  }
  return url.toString();
}

function headersFor(anonymous: boolean, hasBody: boolean, staff?: StaffCredential): Headers {
  const headers = new Headers();
  if (hasBody) headers.set("content-type", "application/json");
  headers.set("x-surface", getSurface());
  const branchId = staff ? staff.branchId : getActiveBranchId();
  if (branchId) headers.set("x-sede-activa", branchId);
  if (!anonymous) {
    const token = staff ? staff.accessToken : getAccessToken();
    if (token) headers.set("authorization", `Bearer ${token}`);
  }
  return headers;
}

async function toApiError(response: Response): Promise<ApiError> {
  let payload: Partial<ErrorResponse> = {};
  try {
    payload = (await response.json()) as Partial<ErrorResponse>;
  } catch {
    /* respuesta sin JSON: nos quedamos con el status */
  }
  const error = payload.error;
  return new ApiError({
    code: error?.code ?? (response.status === 403 ? "FORBIDDEN" : "INTERNAL"),
    message: error?.message ?? `Error ${response.status}`,
    status: response.status,
    rule: error?.rule,
    details: error?.details,
  });
}

// ── Refresh rotativo con cola (contrato §1) ─────────────────────────────────

let refreshInFlight: Promise<boolean> | null = null;

/**
 * Rota el refresh token. **Una sola vez a la vez**: reusar un refresh ya rotado revoca
 * toda la cadena (docs/api/roles.md), así que dos requests que fallan juntos no pueden
 * disparar dos refresh.
 */
export function refreshTokens(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) return false;
    try {
      const response = await fetch(buildUrl("/api/auth/refresh"), {
        method: "POST",
        headers: { "content-type": "application/json", "x-surface": getSurface() },
        body: JSON.stringify({ refreshToken }),
      });
      if (!response.ok) return false;
      const tokens = (await response.json()) as { accessToken: string; refreshToken: string };
      setTokens(tokens);
      return true;
    } catch {
      return false;
    } finally {
      // Se limpia en el microtask siguiente para que los que esperaban lean el resultado.
      queueMicrotask(() => {
        refreshInFlight = null;
      });
    }
  })();

  return refreshInFlight;
}

async function send(path: string, options: RequestOptions, retry: boolean): Promise<Response> {
  // «Ver como rol»: la sesión sigue siendo la del Dueño, así que en la vista previa
  // ninguna escritura sale del navegador (`auth/preview.ts`).
  if (previewBlocks(options.method, path)) {
    throw new ApiError({
      code: "PREVIEW_READ_ONLY",
      message: PREVIEW_BLOCKED_MESSAGE,
      status: 403,
    });
  }
  const hasBody = options.body !== undefined;
  let response: Response;
  try {
    response = await fetch(buildUrl(path, options.query), {
      method: options.method ?? "GET",
      headers: headersFor(options.anonymous ?? false, hasBody, options.staff),
      ...(hasBody ? { body: JSON.stringify(options.body) } : {}),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === "AbortError") throw cause;
    throw new ApiError({
      code: "NETWORK",
      message: "No se pudo hablar con el servidor. Revisá la conexión.",
      status: 0,
    });
  }

  if (response.status === 401 && options.staff) {
    // La sesión prestada no se refresca ni cierra la del navegador: se avisa y listo.
    options.staff.onUnauthorized();
    return response;
  }

  if (response.status === 401 && retry && !options.anonymous) {
    const refreshed = await refreshTokens();
    if (refreshed) return send(path, options, false);
    clearSession();
  }

  return response;
}

/** Request tipado. Devuelve el JSON del backend o lanza `ApiError`. */
export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await send(path, options, true);
  if (!options.anonymous && !options.staff) touchActivity();

  if (!response.ok) throw await toApiError(response);
  if (response.status === 204 || options.raw) return undefined as T;

  const text = await response.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

/** Descarga un export (`?format=csv`) respetando auth y headers de superficie/sede. */
export async function apiBlob(
  path: string,
  query?: RequestOptions["query"],
): Promise<{ blob: Blob; filename: string }> {
  const response = await send(path, { query }, true);
  if (!response.ok) throw await toApiError(response);
  const disposition = response.headers.get("content-disposition") ?? "";
  const match = /filename="?([^"]+)"?/.exec(disposition);
  return {
    blob: await response.blob(),
    filename: match?.[1] ?? "export.csv",
  };
}

type HttpOptions = Omit<RequestOptions, "method" | "body">;

function httpClient(staff?: StaffCredential) {
  return {
    get: <T>(path: string, options?: HttpOptions) =>
      api<T>(path, { ...options, method: "GET", staff }),
    post: <T>(path: string, body?: unknown, options?: HttpOptions) =>
      api<T>(path, { ...options, method: "POST", body, staff }),
    patch: <T>(path: string, body?: unknown, options?: HttpOptions) =>
      api<T>(path, { ...options, method: "PATCH", body, staff }),
    put: <T>(path: string, body?: unknown, options?: HttpOptions) =>
      api<T>(path, { ...options, method: "PUT", body, staff }),
    delete: <T>(path: string, options?: HttpOptions) =>
      api<T>(path, { ...options, method: "DELETE", staff }),
  };
}

export type Http = ReturnType<typeof httpClient>;

/** Cliente con la sesión del navegador. */
export const http: Http = httpClient();

/**
 * El mismo cliente, pero con una credencial de staff propia en vez de la sesión del
 * navegador: la persona que opera la tablet de recepción por un rato (VTCS-91).
 */
export function createStaffHttp(staff: StaffCredential): Http {
  return httpClient(staff);
}
